import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import PublisherImageLibraryModal from './PublisherImageLibraryModal';
import { logoBgColor, LOGO_BG_OPTIONS } from '../utils/logoBg';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const PUBLISHER_JWT_KEY = 'publisher_jwt';
const getToken = () => localStorage.getItem(PUBLISHER_JWT_KEY) || '';

// Logo upload constraints (mirrors backend allow-list + 2 MB cap, design §7).
const LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const LOGO_ALLOWED_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'svg'];
const LOGO_TYPE_MSG = 'Only PNG/JPG/WEBP/SVG up to 2 MB.';

// Neutral inline placeholder for the preview when there is no logo or the URL is dead.
const LOGO_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96">' +
      '<rect width="96" height="96" fill="%23191926"/>' +
      '<path d="M24 64l16-19 12 13 10-10 10 16z" fill="%23333349"/>' +
      '<circle cx="36" cy="34" r="7" fill="%23333349"/>' +
    '</svg>'
  );

// ─── Styled components ────────────────────────────────────────────────────────

const Wrap = styled.div`color: white;`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const NewBtn = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 10px 20px;
  cursor: pointer;
  &:hover { background: #e0404f; }
`;

const Section = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const PubTable = styled.table`width: 100%; border-collapse: collapse;`;

const Th = styled.th`
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255,255,255,0.3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0 0 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const Td = styled.td`
  padding: 12px 0;
  font-size: 0.88rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.8);
  vertical-align: top;
`;

const StatusPill = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  background: ${p => p.$active ? 'rgba(46,204,113,0.15)' : 'rgba(255,200,0,0.1)'};
  color: ${p => p.$active ? '#2ecc71' : '#f0c020'};
  border: 1px solid ${p => p.$active ? 'rgba(46,204,113,0.3)' : 'rgba(255,200,0,0.25)'};
`;

const ActionBtn = styled.button`
  background: none;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 7px;
  color: rgba(255,255,255,0.5);
  font-size: 0.78rem;
  padding: 4px 10px;
  cursor: pointer;
  margin-right: 6px;
  &:hover:not(:disabled) { border-color: rgba(255,255,255,0.3); color: white; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Empty = styled.p`color: rgba(255,255,255,0.25); font-size: 0.88rem; margin: 0;`;
const ErrMsg = styled.p`color: #ff6b7a; font-size: 0.82rem; margin: 10px 0 0;`;
const OkMsg = styled.p`color: #2ecc71; font-size: 0.82rem; margin: 10px 0 0;`;

// ─── Form ─────────────────────────────────────────────────────────────────────

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`margin-bottom: 0;`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  background: #10101c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  padding: 10px 12px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const Textarea = styled.textarea`
  width: 100%;
  background: #10101c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  padding: 10px 12px;
  outline: none;
  box-sizing: border-box;
  resize: vertical;
  min-height: 72px;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

// ─── Logo block (dropzone ported from AgencyVideoUploadModal) ───────────────────

const LogoBlock = styled.div`
  display: flex;
  gap: 14px;
  align-items: stretch;

  @media (max-width: 600px) { flex-direction: column; }
`;

const LogoPreview = styled.div`
  flex-shrink: 0;
  width: 140px;
  height: 96px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.1);
  /* White tile when a logo is set (so transparent/dark logos are readable, matching
     the /join page); keep the dark backing for the neutral "no logo" placeholder.
     Wider box + contain so wide wordmark logos display fully instead of being cropped. */
  background: ${p => (p.$hasLogo ? logoBgColor(p.$bg) : '#10101c')};
  padding: ${p => (p.$hasLogo ? '8px' : '0')};
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; }

  @media (max-width: 600px) { width: 88px; height: 88px; }
`;

const LogoDropZone = styled.div`
  flex: 1;
  border: 2px dashed ${p => p.$dragging ? '#ff4757' : p.$disabled ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.15)'};
  border-radius: 12px;
  padding: 18px 16px;
  text-align: center;
  cursor: ${p => p.$disabled ? 'default' : 'pointer'};
  transition: border-color 0.2s, background 0.2s;
  background: ${p => p.$dragging ? 'rgba(255,71,87,0.05)' : 'rgba(255,255,255,0.02)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: ${p => p.$disabled ? 0.6 : 1};
  &:hover { border-color: ${p => p.$disabled ? 'rgba(255,255,255,0.08)' : 'rgba(255,71,87,0.5)'}; }
`;

const DropText = styled.div`
  font-size: 0.85rem;
  color: rgba(255,255,255,0.5);
  margin-top: 4px;
`;

const DropHint = styled.div`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.28);
  margin-top: 4px;
`;

const LogoActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const ProgressBar = styled.div`
  height: 4px;
  background: rgba(255,255,255,0.08);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 10px;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${p => p.$pct}%;
  background: #ff4757;
  border-radius: 2px;
  transition: width 0.3s;
`;

const QuillWrapper = styled.div`
  .ql-toolbar {
    background: #10101c;
    border: 1px solid rgba(255,255,255,0.1) !important;
    border-bottom: 1px solid rgba(255,255,255,0.06) !important;
    border-radius: 8px 8px 0 0;
  }
  .ql-container {
    background: #10101c;
    border: 1px solid rgba(255,255,255,0.1) !important;
    border-top: none !important;
    border-radius: 0 0 8px 8px;
    font-family: inherit;
    font-size: 0.9rem;
  }
  .ql-editor {
    color: white;
    min-height: 96px;
    p { margin: 0; }
  }
  .ql-editor.ql-blank::before {
    color: rgba(255,255,255,0.2);
    font-style: normal;
  }
  .ql-toolbar .ql-stroke { stroke: rgba(255,255,255,0.55); }
  .ql-toolbar .ql-fill { fill: rgba(255,255,255,0.55); }
  .ql-toolbar button:hover .ql-stroke,
  .ql-toolbar button.ql-active .ql-stroke { stroke: white; }
  .ql-toolbar button:hover .ql-fill,
  .ql-toolbar button.ql-active .ql-fill { fill: white; }
  .ql-toolbar .ql-picker-label { color: rgba(255,255,255,0.55); }
  .ql-toolbar .ql-picker-options { background: #1a1a2e; border-color: rgba(255,255,255,0.1); }
  &:focus-within .ql-toolbar,
  &:focus-within .ql-container { border-color: #ff4757 !important; }
`;

const SlugPreview = styled.div`
  font-size: 0.78rem;
  color: rgba(255,255,255,0.3);
  margin-top: 4px;
  font-family: 'SF Mono', monospace;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const Toggle = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #ff4757;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const RadioRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  color: rgba(255,255,255,0.65);
  input { margin-top: 2px; cursor: pointer; accent-color: #ff4757; flex-shrink: 0; }
`;

const HelperText = styled.p`
  font-size: 0.78rem;
  color: rgba(255,255,255,0.3);
  margin: 6px 0 0;
  line-height: 1.5;
`;

const InsertRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.35);
`;

const PreviewBlock = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(255,255,255,0.06);
`;

const PreviewLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255,255,255,0.3);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
`;

const PreviewSubject = styled.div`
  font-size: 0.9rem;
  color: rgba(255,255,255,0.85);
  margin-bottom: 10px;
  span { color: rgba(255,255,255,0.35); }
`;

const PreviewBody = styled.div`
  color: rgba(255,255,255,0.7);
  font-size: 0.92rem;
  line-height: 1.65;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  padding: 14px 18px;
  text-align: left;
  p { margin: 0 0 0.6em; &:last-child { margin-bottom: 0; } }
  strong { color: rgba(255,255,255,0.9); font-weight: 700; }
  em { font-style: italic; }
  u { text-decoration: underline; }
  ul, ol { margin: 0.4em 0 0.6em 1.4em; padding: 0; }
  li { margin: 0.15em 0; }
  a { color: #65c2d5; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PreviewEmpty = styled.p`
  color: rgba(255,255,255,0.25);
  font-size: 0.88rem;
  font-style: italic;
  margin: 0;
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
`;

const SaveBtn = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 10px 22px;
  cursor: pointer;
  &:hover:not(:disabled) { background: #e0404f; }
  &:disabled { background: #333; color: #555; cursor: default; }
`;

const CancelBtn = styled.button`
  background: none;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px;
  color: rgba(255,255,255,0.45);
  font-size: 0.88rem;
  padding: 10px 18px;
  cursor: pointer;
  &:hover { color: white; border-color: rgba(255,255,255,0.3); }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function slugify(name) {
  return name.trim().toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'publication';
}

const EMPTY_FORM = { name: '', description: '', invite_message: '', logo_url: '', logo_bg: 'light', color: '#ff4757', active: true, points_per_view: 30, signup_limit: '', signup_full_message: '', sort_order: '', welcome_email_enabled: false, welcome_email_subject: '', welcome_email_html: '', welcome_email_mode: 'manual' };

const QUILL_MODULES = { toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link', 'clean']] };
const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

// Normalize Quill output. Strips the whole-empty-editor placeholder, and removes
// an empty paragraph / stray <br> that Quill leaves immediately before a list
// (e.g. `<p>…</p><p><br></p><ul>…`), which renders as an extra blank line before
// the first list item in both the live preview and the sent email. Conservative:
// only the empty-block-directly-before-<ul>/<ol> pattern is collapsed.
const EMPTY_P_BEFORE_LIST_RE = /(?:<p>\s*(?:<br\s*\/?>)?\s*<\/p>|<br\s*\/?>)\s*(?=<(?:ul|ol)\b)/gi;
const normalizeRichText = (val) => {
  if (val === '<p><br></p>') return '';
  return (val || '').replace(EMPTY_P_BEFORE_LIST_RE, '');
};

// Canonical merge-token regex — must match the backend render (Design 028 §3).
const MERGE_TOKEN_RE = /\{\{\s*(subscription_name|publisher_name)\s*\}\}/g;
const SAMPLE_MERGE = { subscription_name: 'USA Now Daily', publisher_name: 'Acme Media' };
const MERGE_TOKENS = ['{{subscription_name}}', '{{publisher_name}}'];

// Pure sample substitution for the live preview (substitute, then sanitize at call site).
const applyMerge = (str) => (str || '').replace(MERGE_TOKEN_RE, (m, key) => SAMPLE_MERGE[key] ?? m);

// ─── Publication form ─────────────────────────────────────────────────────────

const PubForm = ({ initial, onSave, onCancel, saving, error, isEdit, onSendTest, sendingTest, testResult, signupCount, onRecount, recounting }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const subjectRef = useRef(null);
  const bodyQuillRef = useRef(null);

  // ── Logo upload / library state ──
  const [logoDragging, setLogoDragging] = useState(false);
  const [logoStatus, setLogoStatus] = useState('idle'); // 'idle' | 'uploading' | 'success' | 'error'
  const [logoPct, setLogoPct] = useState(0);
  const [logoError, setLogoError] = useState('');
  const [showLibrary, setShowLibrary] = useState(false);

  const uploading = logoStatus === 'uploading';

  const validateLogoFile = (f) => {
    if (!f) return 'No file selected.';
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!LOGO_ALLOWED_EXTS.includes(ext)) return LOGO_TYPE_MSG;
    if (f.size > LOGO_MAX_BYTES) return LOGO_TYPE_MSG;
    return null;
  };

  const uploadLogo = useCallback((f) => {
    const err = validateLogoFile(f);
    if (err) { setLogoError(err); setLogoStatus('error'); return; }
    setLogoError('');
    setLogoStatus('uploading');
    setLogoPct(0);

    const data = new FormData();
    data.append('file', f);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/publisher/images`);
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setLogoPct(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const res = JSON.parse(xhr.responseText);
          set('logo_url', res.url);
          setLogoStatus('success');
          setTimeout(() => setLogoStatus(s => (s === 'success' ? 'idle' : s)), 3000);
        } catch {
          setLogoError('Upload succeeded but the response was invalid.');
          setLogoStatus('error');
        }
      } else if (xhr.status === 413) {
        setLogoError('That image is too large — max 2 MB.');
        setLogoStatus('error');
      } else if (xhr.status === 400) {
        setLogoError(LOGO_TYPE_MSG);
        setLogoStatus('error');
      } else {
        setLogoError(`Upload failed (${xhr.status}).`);
        setLogoStatus('error');
      }
    };
    xhr.onerror = () => { setLogoError('Upload network error. Try again.'); setLogoStatus('error'); };
    xhr.send(data);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoDrop = useCallback((e) => {
    e.preventDefault();
    setLogoDragging(false);
    if (uploading) return;
    const f = e.dataTransfer.files?.[0];
    if (f) uploadLogo(f);
  }, [uploading, uploadLogo]);

  const handleLogoDragOver = (e) => { e.preventDefault(); if (!uploading) setLogoDragging(true); };
  const handleLogoDragLeave = () => setLogoDragging(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const slug = slugify(form.name || '');

  // Insert a merge token into the subject at the caret (fallback: append).
  const insertSubjectToken = (token) => {
    const el = subjectRef.current;
    const current = form.welcome_email_subject || '';
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = current.slice(0, start) + token + current.slice(end);
      set('welcome_email_subject', next);
      requestAnimationFrame(() => {
        try {
          el.focus();
          const pos = start + token.length;
          el.setSelectionRange(pos, pos);
        } catch { /* ignore */ }
      });
    } else {
      set('welcome_email_subject', current + token);
    }
  };

  // Insert a merge token into the Quill body at the caret (fallback: insert at end).
  const insertBodyToken = (token) => {
    const editor = bodyQuillRef.current && bodyQuillRef.current.getEditor
      ? bodyQuillRef.current.getEditor()
      : null;
    if (editor) {
      const sel = editor.getSelection(true);
      const index = sel ? sel.index : editor.getLength();
      editor.insertText(index, token);
      editor.setSelection(index + token.length, 0);
    } else {
      set('welcome_email_html', (form.welcome_email_html || '') + token);
    }
  };

  // Live preview: substitute sample values THEN sanitize (Design 028 §8.3).
  const previewSubject = applyMerge(form.welcome_email_subject || '');
  const normalizedBody = normalizeRichText(form.welcome_email_html);
  const previewBodyHtml = useMemo(
    () => DOMPurify.sanitize(applyMerge(normalizedBody)),
    [normalizedBody]
  );

  // Optional soft token-lint: any {{...}} not in the known set.
  const unknownToken = useMemo(() => {
    const all = `${form.welcome_email_subject || ''} ${form.welcome_email_html || ''}`.match(/\{\{[^}]*\}\}/g) || [];
    return all.some(t => !/\{\{\s*(subscription_name|publisher_name)\s*\}\}/.test(t));
  }, [form.welcome_email_subject, form.welcome_email_html]);

  const subjectLen = (form.welcome_email_subject || '').length;
  const bodyLen = normalizedBody.replace(/<[^>]*>/g, '').length;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <FormGrid>
        <Field style={{ gridColumn: '1 / -1' }}>
          <Label>Publication name *</Label>
          <Input
            value={form.name}
            onChange={e => set('name', e.target.value)}
            placeholder="USA Now Daily"
            required
            autoFocus
          />
          {form.name && <SlugPreview>Slug: {slug}</SlugPreview>}
        </Field>

        <Field style={{ gridColumn: '1 / -1' }}>
          <Label>Description</Label>
          <Textarea
            value={form.description}
            onChange={e => set('description', e.target.value)}
            placeholder="Short description of this publication…"
          />
        </Field>

        <Field style={{ gridColumn: '1 / -1' }}>
          <Label>Invite page message</Label>
          <QuillWrapper>
            <ReactQuill
              theme="snow"
              value={form.invite_message}
              onChange={value => set('invite_message', value)}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Welcome message shown to subscribers on the join page…"
            />
          </QuillWrapper>
          <SlugPreview>Shown on the /join invite page below the publication name</SlugPreview>
        </Field>

        <Field style={{ gridColumn: '1 / -1' }}>
          <Label>Logo</Label>
          <LogoBlock>
            <LogoPreview $hasLogo={!!form.logo_url} $bg={form.logo_bg}>
              <img
                src={form.logo_url || LOGO_PLACEHOLDER}
                alt={form.logo_url ? 'Publication logo preview' : 'No logo selected'}
                onError={e => { e.currentTarget.src = LOGO_PLACEHOLDER; }}
              />
            </LogoPreview>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <LogoDropZone
                $dragging={logoDragging}
                $disabled={uploading}
                onDrop={handleLogoDrop}
                onDragOver={handleLogoDragOver}
                onDragLeave={handleLogoDragLeave}
                onClick={() => { if (!uploading) document.getElementById('publisher-logo-input').click(); }}
              >
                <input
                  id="publisher-logo-input"
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml"
                  style={{ display: 'none' }}
                  onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0])}
                />
                <div style={{ fontSize: '1.5rem' }}>⤴</div>
                <DropText>
                  {uploading ? 'Uploading…' : 'Drag & drop an image, or click to upload'}
                </DropText>
                <DropHint>PNG · JPG · WEBP · SVG · max 2 MB</DropHint>
              </LogoDropZone>
              {uploading && (
                <ProgressBar><ProgressFill $pct={logoPct} /></ProgressBar>
              )}
              <LogoActions>
                <ActionBtn type="button" disabled={uploading} onClick={() => setShowLibrary(true)}>
                  🗂 Choose from library
                </ActionBtn>
                <ActionBtn
                  type="button"
                  disabled={uploading || !form.logo_url}
                  onClick={() => { set('logo_url', ''); setLogoStatus('idle'); setLogoError(''); }}
                >
                  × Remove logo
                </ActionBtn>
              </LogoActions>
              {logoStatus === 'error' && <ErrMsg>{logoError}</ErrMsg>}
              {logoStatus === 'success' && <OkMsg>Logo uploaded</OkMsg>}
            </div>
          </LogoBlock>
          {form.logo_url && (
            <div style={{ marginTop: 14 }}>
              <Label>Logo background</Label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {LOGO_BG_OPTIONS.map(opt => {
                  const activeBg = (form.logo_bg || 'light') === opt.value;
                  return (
                    <ActionBtn
                      key={opt.value}
                      type="button"
                      onClick={() => set('logo_bg', opt.value)}
                      style={activeBg ? { borderColor: '#ff4757', color: '#fff' } : undefined}
                    >
                      {opt.label}
                    </ActionBtn>
                  );
                })}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
                Pick what looks best in the preview on the dark theme — Light tile for dark logos, Transparent for light or white logos.
              </div>
            </div>
          )}

          <Label style={{ marginTop: 14 }}>Logo URL (advanced — paste a hosted image URL)</Label>
          <Input
            value={form.logo_url}
            onChange={e => set('logo_url', e.target.value)}
            placeholder="https://cdn.example.com/logo.png"
          />
        </Field>

        <Field>
          <Label>Brand colour</Label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
              style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', background: 'none' }} />
            <Input value={form.color} onChange={e => set('color', e.target.value)} placeholder="#ff4757"
              style={{ flex: 1 }} />
          </div>
        </Field>

        <Field>
          <Label>Starting balance</Label>
          <Input
            type="number"
            value={form.points_per_view}
            onChange={e => set('points_per_view', Number(e.target.value))}
            min="0"
            placeholder="30"
          />
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
            Points a new subscriber starts with when they join this publication (default 30).
          </div>
        </Field>

        {/* Design 037: signup cap on the open invite link */}
        <Field style={{ gridColumn: '1 / -1' }}>
          <Label>Signup limit (open invite link)</Label>
          <Input
            type="number"
            min="0"
            value={form.signup_limit}
            onChange={e => set('signup_limit', e.target.value)}
            placeholder="No limit"
            style={{ maxWidth: 220 }}
          />
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
            Caps subscribers joining via the shared invite link. Leave blank or 0 for no limit. Your direct email invites still work when full.
          </div>
          {isEdit && Number(form.signup_limit) > 0 && (
            <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span>Currently signed up: <strong style={{ color: '#fff' }}>{signupCount ?? 0} / {form.signup_limit}</strong></span>
              <ActionBtn type="button" disabled={recounting} onClick={() => onRecount && onRecount()}>
                {recounting ? 'Recalculating…' : 'Recalculate'}
              </ActionBtn>
            </div>
          )}
        </Field>

        {Number(form.signup_limit) > 0 && (
          <Field style={{ gridColumn: '1 / -1' }}>
            <Label>Message shown when full</Label>
            <Textarea
              value={form.signup_full_message}
              onChange={e => set('signup_full_message', e.target.value)}
              maxLength={500}
              placeholder="e.g. We're not accepting new signups right now — check back soon!"
            />
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
              {(form.signup_full_message || '').length}/500 · Plain text · {'{{subscription_name}}'} supported · Leave blank for the default message.
            </div>
          </Field>
        )}

        <Field>
          <Label>Sort order</Label>
          <Input
            type="number"
            value={form.sort_order}
            onChange={e => set('sort_order', e.target.value)}
            placeholder="100"
          />
        </Field>

        <Field style={{ gridColumn: '1 / -1' }}>
          <Label>Status</Label>
          <ToggleRow>
            <Toggle checked={form.active} onChange={e => set('active', e.target.checked)} />
            <span style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.6)' }}>
              {form.active ? 'Active' : 'Coming soon (inactive)'}
            </span>
          </ToggleRow>
        </Field>

        <Field style={{ gridColumn: '1 / -1' }}>
          <SectionTitle style={{ marginBottom: 14 }}>Welcome email</SectionTitle>
          <ToggleRow>
            <Toggle
              checked={!!form.welcome_email_enabled}
              onChange={e => set('welcome_email_enabled', e.target.checked)}
            />
            <span style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.6)' }}>
              Send a welcome email to new subscribers
            </span>
          </ToggleRow>

          {form.welcome_email_enabled && (
            <div style={{ marginTop: 16 }}>
              <Label>When someone subscribes</Label>
              <RadioGroup>
                <RadioRow>
                  <input
                    type="radio"
                    name="welcome_email_mode"
                    value="auto"
                    checked={form.welcome_email_mode === 'auto'}
                    onChange={() => set('welcome_email_mode', 'auto')}
                  />
                  Send automatically when someone subscribes
                </RadioRow>
                <RadioRow>
                  <input
                    type="radio"
                    name="welcome_email_mode"
                    value="manual"
                    checked={form.welcome_email_mode !== 'auto'}
                    onChange={() => set('welcome_email_mode', 'manual')}
                  />
                  I'll send manually from the subscriber list
                </RadioRow>
              </RadioGroup>
              <HelperText>
                {form.welcome_email_mode === 'auto'
                  ? 'New subscribers are emailed automatically.'
                  : 'Send each welcome from the Subscribers tab.'}
              </HelperText>
              <HelperText>
                Sent from Paul Soldera &lt;paulsoldera@vusub.com&gt; · Reply-To set at app level
              </HelperText>

              <div style={{ marginTop: 18 }}>
                <Label>Subject *</Label>
                <InsertRow>
                  <span>Insert:</span>
                  {MERGE_TOKENS.map(t => (
                    <ActionBtn key={t} type="button" onClick={() => insertSubjectToken(t)}>{t}</ActionBtn>
                  ))}
                </InsertRow>
                <Input
                  ref={subjectRef}
                  value={form.welcome_email_subject}
                  onChange={e => set('welcome_email_subject', e.target.value)}
                  placeholder="Welcome to {{subscription_name}}"
                />
                <SlugPreview style={{ color: subjectLen > 80 ? '#f0a020' : undefined }}>
                  {subjectLen} chars{subjectLen > 80 ? ' — keep it under ~80 for inbox previews' : ''}
                </SlugPreview>
              </div>

              <div style={{ marginTop: 18 }}>
                <Label>Email body</Label>
                <InsertRow>
                  <span>Insert:</span>
                  {MERGE_TOKENS.map(t => (
                    <ActionBtn key={t} type="button" onClick={() => insertBodyToken(t)}>{t}</ActionBtn>
                  ))}
                </InsertRow>
                <QuillWrapper>
                  <ReactQuill
                    ref={bodyQuillRef}
                    theme="snow"
                    value={form.welcome_email_html}
                    onChange={value => set('welcome_email_html', value)}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Hi — thanks for subscribing to {{subscription_name}}!"
                  />
                </QuillWrapper>
                <SlugPreview style={{ color: bodyLen > 1500 ? '#f0a020' : undefined }}>
                  {bodyLen} chars{bodyLen > 1500 ? ' — consider trimming for readability' : ''}
                </SlugPreview>
                {unknownToken && (
                  <HelperText style={{ color: '#f0a020' }}>
                    An unrecognised {'{{token}}'} will be sent literally. Only {'{{subscription_name}}'} and {'{{publisher_name}}'} are substituted.
                  </HelperText>
                )}
              </div>

              <PreviewBlock>
                <PreviewLabel>Preview (sample values)</PreviewLabel>
                <PreviewSubject>
                  <span>Subject:</span> {previewSubject || <span>(no subject)</span>}
                </PreviewSubject>
                {previewBodyHtml
                  ? <PreviewBody dangerouslySetInnerHTML={{ __html: previewBodyHtml }} />
                  : <PreviewEmpty>Email body is empty</PreviewEmpty>}
              </PreviewBlock>

              <FormActions>
                <ActionBtn
                  type="button"
                  disabled={!isEdit || sendingTest}
                  onClick={() => onSendTest && onSendTest(form)}
                >
                  {sendingTest ? 'Sending…' : 'Send test email'}
                </ActionBtn>
                {!isEdit && (
                  <HelperText style={{ margin: '4px 0 0' }}>Save the publication first to send a test.</HelperText>
                )}
                {testResult && testResult.status === 'sent' && (
                  <OkMsg style={{ margin: 0, alignSelf: 'center' }}>✓ Sent to {testResult.sentTo}</OkMsg>
                )}
                {testResult && testResult.status === 'error' && (
                  <ErrMsg style={{ margin: 0, alignSelf: 'center' }}>{testResult.error}</ErrMsg>
                )}
              </FormActions>
            </div>
          )}
        </Field>
      </FormGrid>

      {error && <ErrMsg>{error}</ErrMsg>}

      <FormActions>
        <SaveBtn type="submit" disabled={saving || uploading || !form.name.trim()}>
          {saving ? 'Saving…' : uploading ? 'Uploading logo…' : 'Save publication'}
        </SaveBtn>
        <CancelBtn type="button" onClick={onCancel}>Cancel</CancelBtn>
      </FormActions>

      {showLibrary && (
        <PublisherImageLibraryModal
          token={getToken()}
          onSelect={(url) => { set('logo_url', url); setLogoStatus('idle'); setLogoError(''); }}
          onClose={() => setShowLibrary(false)}
        />
      )}
    </form>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const PublisherPublications = ({ isOwner }) => {
  const [pubs, setPubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recounting, setRecounting] = useState(false); // design 037: signup recount in-flight
  const [formError, setFormError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null); // { status: 'sent'|'error', sentTo?, error? }

  const fetchPubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPubs(data.publications || []);
      }
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPubs(); }, [fetchPubs]);

  const handleCreate = async (form) => {
    setFormError('');
    if (form.welcome_email_enabled && !(form.welcome_email_subject || '').trim()) {
      setFormError('A subject is required to enable the welcome email.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          name: form.name.trim(),
          description: form.description || undefined,
          invite_message: normalizeRichText(form.invite_message) || undefined,
          logo_url: form.logo_url || undefined,
          logo_bg: form.logo_bg || undefined,
          color: form.color || undefined,
          active: form.active,
          points_per_view: Number(form.points_per_view) || 30,
          signup_limit: Number(form.signup_limit) || 0,
          signup_full_message: form.signup_full_message || undefined,
          sort_order: form.sort_order !== '' ? Number(form.sort_order) : undefined,
          welcome_email_enabled: !!form.welcome_email_enabled,
          welcome_email_subject: form.welcome_email_subject || undefined,
          welcome_email_html: normalizeRichText(form.welcome_email_html) || undefined,
          welcome_email_mode: form.welcome_email_mode === 'auto' ? 'auto' : 'manual',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setShowCreate(false);
      setStatusMsg(`"${data.name}" created`);
      setTimeout(() => setStatusMsg(''), 4000);
      fetchPubs();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (form) => {
    setFormError('');
    if (form.welcome_email_enabled && !(form.welcome_email_subject || '').trim()) {
      setFormError('A subject is required to enable the welcome email.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${editId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          name: form.name.trim() || undefined,
          description: form.description !== undefined ? form.description : undefined,
          invite_message: form.invite_message !== undefined ? normalizeRichText(form.invite_message) : undefined,
          logo_url: form.logo_url !== undefined ? form.logo_url : undefined,
          logo_bg: form.logo_bg !== undefined ? form.logo_bg : undefined,
          color: form.color !== undefined ? form.color : undefined,
          active: form.active,
          points_per_view: Number(form.points_per_view) || undefined,
          signup_limit: form.signup_limit === '' ? 0 : (Number(form.signup_limit) || 0),
          signup_full_message: form.signup_full_message !== undefined ? form.signup_full_message : undefined,
          sort_order: form.sort_order !== '' ? Number(form.sort_order) : undefined,
          welcome_email_enabled: !!form.welcome_email_enabled,
          welcome_email_subject: form.welcome_email_subject !== undefined ? (form.welcome_email_subject || undefined) : undefined,
          welcome_email_html: form.welcome_email_html !== undefined ? (normalizeRichText(form.welcome_email_html) || undefined) : undefined,
          welcome_email_mode: form.welcome_email_mode === 'auto' ? 'auto' : 'manual',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setEditId(null);
      setStatusMsg('Publication updated');
      setTimeout(() => setStatusMsg(''), 4000);
      fetchPubs();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async (form) => {
    if (!editId) return;
    setTestResult(null);
    setSendingTest(true);
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${editId}/welcome-email/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          subject: form.welcome_email_subject,
          html: normalizeRichText(form.welcome_email_html),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setTestResult({ status: 'sent', sentTo: data.sent_to });
      setTimeout(() => setTestResult(null), 4000);
    } catch (err) {
      setTestResult({ status: 'error', error: err.message });
    } finally {
      setSendingTest(false);
    }
  };

  const handleToggleActive = async (pub) => {
    try {
      await fetch(`${API_URL}/api/publisher/publications/${pub.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ active: !pub.active }),
      });
      fetchPubs();
    } catch { /* non-critical */ }
  };

  const handleRecount = async (pubId) => {
    setRecounting(true);
    try {
      const res = await fetch(`${API_URL}/api/publisher/publications/${pubId}/recount-signups`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      await fetchPubs();
    } catch { /* non-critical — the readout just won't refresh */ } finally {
      setRecounting(false);
    }
  };

  const editingPub = editId ? pubs.find(p => p.id === editId) : null;

  return (
    <Wrap>
      <TitleRow>
        <Title>Publications</Title>
        {isOwner && !showCreate && !editId && (
          <NewBtn onClick={() => setShowCreate(true)}>+ New publication</NewBtn>
        )}
      </TitleRow>

      {statusMsg && <OkMsg style={{ marginBottom: 16 }}>{statusMsg}</OkMsg>}

      {showCreate && isOwner && (
        <Section>
          <SectionTitle>New publication</SectionTitle>
          <PubForm
            isEdit={false}
            onSave={handleCreate}
            onCancel={() => { setShowCreate(false); setFormError(''); }}
            saving={saving}
            error={formError}
          />
        </Section>
      )}

      {editId && editingPub && (
        <Section>
          <SectionTitle>Edit — {editingPub.name}</SectionTitle>
          <PubForm
            initial={{
              name: editingPub.name || '',
              description: editingPub.description || '',
              invite_message: editingPub.invite_message || '',
              logo_url: editingPub.logo_url || '',
              logo_bg: editingPub.logo_bg || 'light',
              color: editingPub.color || '#ff4757',
              active: editingPub.active !== false,
              points_per_view: editingPub.points_per_view ?? 30,
              signup_limit: editingPub.signup_limit || '',
              signup_full_message: editingPub.signup_full_message || '',
              sort_order: editingPub.sort_order ?? '',
              welcome_email_enabled: editingPub.welcome_email_enabled === true,
              welcome_email_subject: editingPub.welcome_email_subject || '',
              welcome_email_html: editingPub.welcome_email_html || '',
              welcome_email_mode: editingPub.welcome_email_mode === 'auto' ? 'auto' : 'manual',
            }}
            isEdit
            signupCount={editingPub.active_subscriber_count ?? 0}
            onRecount={() => handleRecount(editingPub.id)}
            recounting={recounting}
            onSave={handleEdit}
            onSendTest={handleSendTest}
            sendingTest={sendingTest}
            testResult={testResult}
            onCancel={() => { setEditId(null); setFormError(''); setTestResult(null); }}
            saving={saving}
            error={formError}
          />
        </Section>
      )}

      <Section>
        <SectionTitle>All publications</SectionTitle>
        {loading ? (
          <Empty>Loading…</Empty>
        ) : pubs.length === 0 ? (
          <Empty>No publications yet.{isOwner ? ' Create your first one above.' : ''}</Empty>
        ) : (
          <PubTable>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Slug</Th>
                {isOwner && <Th>Status</Th>}
                <Th>Start bal</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {pubs.map(p => (
                <tr key={p.id}>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {p.color && (
                        <span style={{ width: 10, height: 10, borderRadius: '50%',
                          background: p.color, flexShrink: 0 }} />
                      )}
                      {p.name}
                    </div>
                  </Td>
                  <Td style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {p.id}
                  </Td>
                  {isOwner && (
                    <Td>
                      <StatusPill $active={p.active !== false}>
                        {p.active !== false ? 'Active' : 'Coming Soon'}
                      </StatusPill>
                    </Td>
                  )}
                  <Td style={{ color: 'rgba(255,255,255,0.45)' }}>{p.points_per_view ?? 30}</Td>
                  <Td>
                    <ActionBtn onClick={() => { setEditId(p.id); setShowCreate(false); setFormError(''); setTestResult(null); }}>
                      Edit
                    </ActionBtn>
                    {isOwner && (
                      <ActionBtn onClick={() => handleToggleActive(p)}>
                        {p.active !== false ? 'Deactivate' : 'Activate'}
                      </ActionBtn>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </PubTable>
        )}
      </Section>
    </Wrap>
  );
};

export default PublisherPublications;
